/**
 * Organization Page Wrapper Component
 *
 * Shared wrapper for organization management pages that handles:
 * - No organization state (shows StartGroupSection)
 * - Loading state
 * - Permission checks (billing admin access)
 */

import { Box, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { ReactNode } from 'react';

import { useOrganizationAccess } from '../store/hooks';
import { StartGroupSection } from './StartGroupSection';

interface OrganizationPageWrapperProps {
  children: ReactNode;
  pageTitle: string;
}

export function OrganizationPageWrapper({ children, pageTitle }: OrganizationPageWrapperProps) {
  const { userIsInGroup, userIsBillingAdmin, orgLoading } = useOrganizationAccess();

  // User is not part of any organization
  if (!userIsInGroup) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, margin: 'auto' }}>
        <StartGroupSection />
      </Box>
    );
  }

  // Loading organization data
  if (orgLoading) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, margin: 'auto', display: 'flex', alignItems: 'center' }}>
        <CircularProgress size={24} />
        <Typography sx={{ ml: 2 }}>Loading organization data...</Typography>
      </Box>
    );
  }

  // User doesn't have billing admin access
  if (!userIsBillingAdmin) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, margin: 'auto' }}>
        <Typography variant="h4" gutterBottom>
          {pageTitle}
        </Typography>
        <Card>
          <CardContent>
            <Typography>
              You don&apos;t have permission to manage this organization. Contact your billing
              administrator for access.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // User has access - render the page content
  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        {pageTitle}
      </Typography>
      {children}
    </Box>
  );
}
